/**
 * Mobile Bottom Tab Navigation
 * Material Design 3 Navigation Bar Pattern
 *
 * Research: Mobile-first tabbed interface with bottom navigation
 * - 3 tabs: Actions, Inventory, Character
 * - Fixed bottom, 56px min-height
 * - Active state: primary color tint + bottom indicator
 * - Safe area insets for notched devices
 * - WCAG AA accessibility (ARIA labels, semantic HTML)
 */

import React from 'react';
import { Sword, Backpack, ScrollText } from 'lucide-react';
import { cn } from '../../lib/utils';

const TABS = [
  { id: 'combat', label: 'Actions', icon: Sword },
  { id: 'inventory', label: 'Inventory', icon: Backpack },
  { id: 'character', label: 'Character', icon: ScrollText },
];

export default function MobileTabNavigation({ activeTab, onTabChange }) {
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-br from-gray-900 to-black border-t-2 border-red-700 shadow-2xl safe-bottom lg:hidden"
      role="tablist"
      aria-label="Character sheet sections"
    >
      <div className="flex justify-around items-stretch min-h-[56px]">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${tab.id}-panel`}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                // Base styles
                'flex-1 flex flex-col items-center justify-center gap-1',
                'min-h-touch-min px-3 py-2',
                'transition-all duration-200',
                'relative',
                // Focus styles (WCAG AA)
                'focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-inset',
                // Active state with gaming aesthetics
                isActive ? 'text-red-400' : 'text-gray-400 hover:text-red-300'
              )}
            >
              {/* Icon */}
              <Icon
                className={cn(
                  'w-6 h-6 transition-transform duration-200',
                  isActive && 'scale-110'
                )}
              />

              {/* Label */}
              <span className="text-tiny font-medium">{tab.label}</span>

              {/* Active indicator (bottom bar) with red accent */}
              {isActive && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-red-700 rounded-t-full shadow-lg shadow-red-700/50"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
